import { useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  toAppRole,
  getPermissions,
  hasPermission,
  backendRoleHasPermission,
  ALL_PERMISSIONS,
  type Permission,
  type AppRole,
  type BackendRole,
} from '@lib/rbac';

export interface UsePermissionsResult {
  /** App-level role derived from the backend userRole. */
  appRole: AppRole;
  /** Full list of permissions for the current user. */
  permissions: readonly Permission[];
  /** Check a single permission. */
  can: (permission: Permission) => boolean;
  /** True when the user is a system admin or operations manager (write-capable admin). */
  isAdmin: boolean;
  /** True when the user is a support agent (read-only with minor edits). */
  isCustomerService: boolean;
}

export function usePermissions(): UsePermissionsResult {
  const { userRole, userPermissions } = useAuth();
  const appRole = useMemo(() => toAppRole(userRole), [userRole]);

  const permissions = useMemo<readonly Permission[]>(() => {
    // Prefer dynamic permissions from the backend when available.
    if (userPermissions && userPermissions.length > 0) {
      const valid = new Set(ALL_PERMISSIONS);
      return userPermissions.filter((p): p is Permission => valid.has(p as Permission));
    }
    return getPermissions(appRole);
  }, [appRole, userPermissions]);

  const can = useMemo(() => {
    const set = new Set(permissions);
    return (permission: Permission) => set.has(permission);
  }, [permissions]);

  return {
    appRole,
    permissions,
    can,
    isAdmin: appRole === 'SYSTEM_ADMIN' || appRole === 'OPERATIONS_MANAGER',
    isCustomerService: appRole === 'SUPPORT_AGENT',
  };
}

export { hasPermission, backendRoleHasPermission, toAppRole, getPermissions, ALL_PERMISSIONS };
export type { Permission, AppRole, BackendRole };
